// AdminSendStudentInvitations.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  CircularProgress,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import api from '../../../API/axiosInstance';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import safeToast from 'utils/safeToast';

ModuleRegistry.registerModules([AllCommunityModule]);
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const AdminSendStudentInvitations = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitationSent, setInvitationSent] = useState(null); // null = all, false = not sent, true = sent
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [sendingInvitations, setSendingInvitations] = useState(false);

  const handleStudentSelection = useCallback((student, checked) => {
    setSelectedStudents(prev => {
      if (checked) {
        return prev.some(s => s.uploadId === student.uploadId) ? prev : [...prev, student];
      } else {
        return prev.filter(s => s.uploadId !== student.uploadId);
      }
    });
  }, []);

  const checkboxCellRenderer = useCallback((params) => {
    if (!params.data) return null;
    
    const student = params.data;
    const isSelected = selectedStudents.some(s => s.uploadId === student.uploadId);
    
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            handleStudentSelection(student, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            padding: '4px',
            '& .MuiSvgIcon-root': {
              fontSize: '18px',
            },
          }}
        />
      </Box>
    );
  }, [selectedStudents, handleStudentSelection]);

  const columnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 60,
      pinned: "left",
      resizable: false,
      sortable: false,
      filter: false,
      lockPosition: true,
    },
    {
      field: "fullName",
      headerName: "Name",
      width: 160,
    },
    {
      field: "email",
      headerName: "Email",
      width: 180,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      width: 110,
    },
    {
      field: "whatsAppNumber",
      headerName: "WhatsApp",
      width: 110,
    },
    {
      field: "batchYear",
      headerName: "Batch",
      width: 80,
    },
    {
      field: "instype",
      headerName: "Inst. Type",
      width: 120,
    },
    {
      field: "intname",
      headerName: "Institute",
      width: 140,
    },
    {
      field: "cat",
      headerName: "Category",
      width: 120,
    },
    {
      field: "dept",
      headerName: "Department",
      width: 140,
    },
    {
      field: "deg",
      headerName: "Degree",
      width: 120,
    },
    {
      field: "coursename",
      headerName: "Course",
      width: 140,
    },
    {
      field: "gender",
      headerName: "Gender",
      width: 90,
    },
    {
      field: "community",
      headerName: "Community",
      width: 120,
    },
    {
      field: "bloodgroup",
      headerName: "Blood Group",
      width: 110,
    },
    {
      field: "dateOfBirth",
      headerName: "DOB",
      width: 100,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString('en-GB');
      },
    },
    {
      field: "country",
      headerName: "Country",
      width: 120,
    },
    {
      field: "state",
      headerName: "State",
      width: 120,
    },
    {
      field: "district",
      headerName: "District",
      width: 120,
    },
    {
      field: "invitationSent",
      headerName: "Invitation Sent",
      cellRenderer: (params) => params.value ? 'Yes' : 'No',
      width: 130,
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    minWidth: 100,
  }), []);

  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
  }, []);

  const fetchCurrentStudents = useCallback(async (invitationSentFilter = null) => {
    try {
      setLoading(true);
      const params = {};
      if (invitationSentFilter !== null) {
        params.invitationSent = invitationSentFilter;
      }
      
      const response = await api.get('/StudentRequest/get-current-students', { params });
      const data = Array.isArray(response.data) ? response.data : [];
      
      // Transform data for grid (convert PascalCase to camelCase)
      const transformedData = data.map(student => {
        const convertKeys = (obj) => {
          const converted = {};
          for (const key in obj) {
            // Handle all uppercase keys (like INSTYPE, INTNAME) or PascalCase
            let camelKey;
            if (key === key.toUpperCase()) {
              // All uppercase - convert to lowercase
              camelKey = key.toLowerCase();
            } else {
              // PascalCase - convert to camelCase
              camelKey = key.charAt(0).toLowerCase() + key.slice(1);
            }
            converted[camelKey] = obj[key];
          }
          return converted;
        };
        const converted = convertKeys(student);
        return {
          uploadId: converted.uploadId,
          erpStudentId: converted.erpStudentId,
          fullName: converted.fullName || `${converted.firstName || ''} ${converted.lastName || ''}`.trim(),
          email: converted.email || '',
          mobile: converted.mobile || '',
          whatsAppNumber: converted.whatsappNumber || converted.whatsAppNumber || '',
          batchYear: converted.batchYear || '',
          invitationSent: converted.invitationSent || false,
          // Master data fields
          instype: converted.instype || '',
          intname: converted.intname || '',
          cat: converted.cat || '',
          dept: converted.dept || '',
          deg: converted.deg || '',
          coursename: converted.coursename || '',
          gender: converted.gender || '',
          community: converted.community || '',
          bloodgroup: converted.bloodgroup || '',
          country: converted.country || '',
          state: converted.state || '',
          district: converted.district || '',
          dateOfBirth: converted.dateOfBirth || null,
        };
      });
      
      setRowData(transformedData);
    } catch (error) {
      console.error("Error fetching current students:", error);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCurrentStudents(invitationSent);
    }
  }, [open, invitationSent, fetchCurrentStudents]);

  const handleSendInvitations = useCallback(async () => {
    if (selectedStudents.length === 0) {
      safeToast.warn('Please select at least one student to send invitations.');
      return;
    }

    setSendingInvitations(true);
    try {
      const uploadIds = selectedStudents.map(s => s.uploadId);
      
      const response = await api.post('/StudentRequest/send-invitations', {
        uploadIds: uploadIds,
        sentBy: 1 // TODO: Get from auth context
      });

      if (response.data) {
        safeToast.success(`Invitations marked as sent for ${selectedStudents.length} students!`);
        setSelectedStudents([]);
        fetchCurrentStudents(invitationSent); // Refresh list
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      safeToast.error('Failed to send invitations. Please try again.');
    } finally {
      setSendingInvitations(false);
    }
  }, [selectedStudents, invitationSent, fetchCurrentStudents]);

  const exportCsv = useCallback(() => {
    if (!rowData || rowData.length === 0) {
      safeToast.warn('No data to export');
      return;
    }

    try {
      const headers = ['S.No', 'Name', 'Email', 'Mobile', 'WhatsApp', 'Batch', 'Inst. Type', 'Institute', 'Category', 'Department', 'Degree', 'Course', 'Gender', 'Community', 'Blood Group', 'DOB', 'Country', 'State', 'District'];
      const csvContent = [
        headers.join(','),
        ...rowData.map((row, index) => [
          index + 1,
          `"${(row.fullName || '').replace(/"/g, '""')}"`,
          `"${(row.email || '').replace(/"/g, '""')}"`,
          `"${(row.mobile || '').replace(/"/g, '""')}"`,
          `"${(row.whatsAppNumber || '').replace(/"/g, '""')}"`,
          `"${(row.batchYear || '').replace(/"/g, '""')}"`,
          `"${(row.instype || '').replace(/"/g, '""')}"`,
          `"${(row.intname || '').replace(/"/g, '""')}"`,
          `"${(row.cat || '').replace(/"/g, '""')}"`,
          `"${(row.dept || '').replace(/"/g, '""')}"`,
          `"${(row.deg || '').replace(/"/g, '""')}"`,
          `"${(row.coursename || '').replace(/"/g, '""')}"`,
          `"${(row.gender || '').replace(/"/g, '""')}"`,
          `"${(row.community || '').replace(/"/g, '""')}"`,
          `"${(row.bloodgroup || '').replace(/"/g, '""')}"`,
          `"${(row.dateOfBirth ? new Date(row.dateOfBirth).toLocaleDateString('en-GB') : '').replace(/"/g, '""')}"`,
          `"${(row.country || '').replace(/"/g, '""')}"`,
          `"${(row.state || '').replace(/"/g, '""')}"`,
          `"${(row.district || '').replace(/"/g, '""')}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `current-students-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
      safeToast.error('Failed to export CSV. Please try again.');
    }
  }, [rowData]);

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen>
      <DialogTitle>
        <MDTypography variant="h5" fontWeight="bold">
          Send Student Invitations
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} sx={{ mt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 200 }}
                options={[
                  { value: null, label: 'All Students' },
                  { value: false, label: 'Not Sent' },
                  { value: true, label: 'Sent' }
                ]}
                value={{ value: invitationSent, label: invitationSent === null ? 'All Students' : invitationSent ? 'Sent' : 'Not Sent' }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setInvitationSent(newValue.value);
                  }
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Invitation Status" />
                )}
              />
              <MDButton
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={exportCsv}
                disabled={rowData.length === 0}
              >
                Export CSV
              </MDButton>
            </Box>
            <MDButton
              variant="contained"
              color="success"
              startIcon={<SendIcon />}
              onClick={handleSendInvitations}
              disabled={selectedStudents.length === 0 || sendingInvitations}
            >
              {sendingInvitations ? 'Sending...' : `Send Invitations (${selectedStudents.length})`}
            </MDButton>
          </Box>

          <div className="ag-theme-alpine" style={{ height: '60vh', width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={[
                {
                  headerName: "",
                  field: "checkbox",
                  width: 50,
                  pinned: "left",
                  cellRenderer: checkboxCellRenderer,
                  headerCheckboxSelection: false,
                  suppressSizeToFit: true,
                  sortable: false,
                  filter: false,
                  resizable: false,
                  lockPosition: true,
                },
                ...columnDefs
              ]}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              animateRows={true}
              suppressRowClickSelection={true}
              rowHeight={35}
              headerHeight={32}
              pagination={true}
              paginationPageSize={20}
              suppressSizeToFit={true}
            />
          </div>
          <style>{`
            .ag-theme-alpine .ag-header-cell-label {
              font-size: 11px;
              font-weight: 600;
              padding: 4px 8px;
            }
            .ag-theme-alpine .ag-cell {
              font-size: 11px;
              padding: 4px 8px;
              line-height: 1.4;
            }
            .ag-theme-alpine .ag-row {
              min-height: 35px;
            }
            .ag-theme-alpine .ag-header-row {
              min-height: 32px;
            }
            .ag-theme-alpine .ag-header-cell {
              padding: 0 4px;
            }
            .ag-theme-alpine .ag-cell-wrapper {
              padding: 0;
            }
            .ag-theme-alpine .ag-cell[col-id="checkbox"] {
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0;
            }
          `}</style>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

AdminSendStudentInvitations.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminSendStudentInvitations;

